import type {
  EntitySearchEntityType,
  EntitySearchParams,
} from 'types/api-payloads';

/**
 * The compatible types a reconciliation can accept, locked at open. A canonical
 * survivor restricts to its own type + Imported (allowedEntityTypes); an
 * Imported-viewed reconciliation with nothing else established stays
 * Imported-only.
 * @param {EntitySearchEntityType[]} [allowedEntityTypes] The types it can still accept.
 * @param {EntitySearchEntityType} [primaryEntityType] The viewed entity's type.
 * @return {EntitySearchEntityType[] | null} The locked types, or null for no lock.
 */
export function entityTypeLock(
  allowedEntityTypes?: EntitySearchEntityType[],
  primaryEntityType?: EntitySearchEntityType
): EntitySearchEntityType[] | null {
  if (allowedEntityTypes && allowedEntityTypes.length > 0) {
    return allowedEntityTypes;
  }
  return primaryEntityType === 'Imported' ? ['Imported'] : null;
}

/**
 * How many filters a search applies, for the panel's count. A type lock is not
 * a filter the reader set, and classification counts only when narrowed from
 * its Primary + Aliases default.
 * @param {EntitySearchParams | undefined} payload The search.
 * @param {boolean} typeFilterLocked Whether the types are locked.
 * @return {number} The count.
 */
export function countEntityFilters(
  payload: EntitySearchParams | undefined,
  typeFilterLocked: boolean
): number {
  if (!payload) return 0;
  let count = 0;
  if (payload.createdAt?.start || payload.createdAt?.end) count += 1;
  if (payload.updatedAt?.start || payload.updatedAt?.end) count += 1;
  if (
    !typeFilterLocked &&
    payload.entityTypes &&
    payload.entityTypes.length > 0
  ) {
    count += payload.entityTypes.length;
  }
  const includePrimary = payload.includePrimary ?? true;
  const includeAliases = payload.includeAliases ?? true;
  if (!(includePrimary && includeAliases)) count += 1;
  return count;
}
